(() => {
  if (customElements.get('fastwet-banner-carousel')) return;
  class FastWetBannerCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = Array.from(this.querySelectorAll('[data-fw-slide]'));
      if (this.slides.length < 2) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.dots = Array.from(this.querySelectorAll('[data-fw-go]'));
      this.pauseButton = this.querySelector('[data-fw-pause]');
      this.status = this.querySelector('[data-fw-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.delay = Math.max(4000, Number(this.dataset.interval) || 6000);
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || Boolean(window.Shopify?.designMode);
      this.hovered = false;
      this.focused = false;
      this.visible = true;
      this.finger = null;
      this.querySelector('[data-fw-prev]')?.addEventListener('click', () => this.show(this.index - 1, true), opts);
      this.querySelector('[data-fw-next]')?.addEventListener('click', () => this.show(this.index + 1, true), opts);
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true), opts));
      this.pauseButton?.addEventListener('pointerdown', () => { this.pauseIntent = !this.paused; }, opts);
      this.pauseButton?.addEventListener('click', () => {
        this.paused = this.pauseIntent ?? !this.paused;
        this.pauseIntent = undefined;
        this.updatePause();
        this.schedule();
      }, opts);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); }
      }, opts);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, opts);
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.schedule();
      }, opts);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.schedule();
      }, opts);
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      }, opts);
      const stage = this.querySelector('.fw-banner-stage');
      stage?.addEventListener('touchstart', event => {
        const touch = event.touches[0];
        this.finger = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
      }, { ...opts, passive: true });
      stage?.addEventListener('touchend', event => {
        if (!this.finger) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.finger.x;
        const dy = touch.clientY - this.finger.y;
        this.finger = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClickUntil = Date.now() + 400;
          this.show(this.index + (dx < 0 ? 1 : -1), true);
        }
      }, { ...opts, passive: true });
      stage?.addEventListener('touchcancel', () => { this.finger = null; }, opts);
      stage?.addEventListener('click', event => {
        if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, { ...opts, capture: true });
      document.addEventListener('visibilitychange', () => this.schedule(), opts);
      this.motion.addEventListener('change', () => {
        this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || Boolean(window.Shopify?.designMode);
        this.updatePause();
        this.schedule();
      }, opts);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.15 });
        this.observer.observe(this);
      }
      this.setAttribute('data-ready', '');
      this.updatePause();
      this.show(0);
    }
    updatePause() {
      if (!this.pauseButton) return;
      const label = this.paused ? 'Play banner rotation' : 'Pause banner rotation';
      this.pauseButton.setAttribute('aria-label', label);
      this.pauseButton.setAttribute('title', label);
      this.pauseButton.querySelector('[data-fw-pause-label]').textContent = this.paused ? 'Play' : 'Pause';
      this.pauseButton.querySelector('[data-fw-pause-icon]').textContent = this.paused ? '\u25b6' : '\u275a\u275a';
    }
    show(index, manual = false) {
      const next = (index + this.slides.length) % this.slides.length;
      const targetImage = this.slides[next].querySelector('img');
      if (!manual && next !== this.index && targetImage && (!targetImage.complete || !targetImage.naturalWidth)) { targetImage.loading = 'eager'; this.schedule(); return; }
      const moveFocus = manual && this.slides[this.index]?.contains(document.activeElement);
      this.index = next;
      this.slides.forEach((slide, i) => {
        const active = i === next;
        slide.toggleAttribute('data-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        const link = slide.querySelector('a');
        if (link) link.tabIndex = active ? 0 : -1;
      });
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));
      if (moveFocus) this.slides[next].querySelector('a')?.focus({ preventScroll: true });
      if (manual) {
        if (this.status) this.status.textContent = `Banner ${next + 1} of ${this.slides.length}: ${this.slides[next].dataset.title}`;
      }
      const upcoming = this.slides[(next + 1) % this.slides.length].querySelector('img');
      if (upcoming) upcoming.loading = 'eager';
      this.schedule();
    }
    schedule() {
      window.clearTimeout(this.timer);
      if (!this.isConnected || this.paused || this.hovered || this.focused || !this.visible || document.hidden) return;
      this.timer = window.setTimeout(() => this.show(this.index + 1), this.delay);
    }
    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
    }
  }
  customElements.define('fastwet-banner-carousel', FastWetBannerCarousel);
})();
